import { Component, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { Observable, forkJoin, of, Subscription, combineLatest } from 'rxjs';
import { MatTableDataSource } from '@angular/material/table';
import { MatDialog } from '@angular/material/dialog';
import { SupplyDetails } from '../../models/supply-details';
import { CustomService } from '../../services/custom.service';
import { QuoteRequestService } from '../../../shared/services/quote-request.service';
import { NavigateArgs, WizardService } from '../../../wizard-bar/services/wizard.service';
import { UsAddressLookupService } from '../../../shared/services/us-address-lookup.service';
import { ConfirmComponent } from '../../../shared/components/confirm/confirm.component';
import { ApplyToAllComponent } from '../../../shared/components/apply-to-all/apply-to-all.component';
import { DateConstants } from '../../../constants/date-constants';
import { ResolvePlcNsplRequirement, IPlcNsplRequirement } from '../../../custom/pages/custom-supply-details/plc-nspl-resolver';
import { Store } from '@ngrx/store';
import { UtilityHubIdentity } from '../../../auth/models/utility-hub-identity';
import { isEqualWith, cloneDeep } from 'lodash';
import { QuoteRequestStatus } from '../../../shared/models/quote-request-status';
import { CommodityType } from '@app/shared/enums/commodity-type';
import { SalesType } from '@app/shared/enums/sales-type';
import { PricingSource, PricingSourceService } from '@app/shared/services/pricing-source.service';
import { Logger } from '@app/logging.service';
import { CustomConstants } from '@app/custom/models/custom-constants';
import { AlertComponent } from '@app/shared/components/alert/alert.component';
import { QuoteRequestType } from '@app/shared/models/QuoteRequestType';
import { AppState } from '@app/state/app.state';
import {
  SetCommodityType,
  SetSalesType,
} from '@utility-information/state/actions/utility-information.actions';
import {
  salesTypeSelector,
  utilityUOMSelector,
  usageSourcesSelector,
} from '@utility-information/state/selectors/utility-information.selectors';
import { isReadOnlySelector, loggedInUserSelector, loggedInUtilityHubIdentitySelector } from '@app/state/app.selectors';
import { UsageSource } from '@app/shared/enums/usage-source';
import { SupplyDetailsModel } from '@app/custom/models/supply-details.model';
import { filter, switchMap, tap } from 'rxjs/operators';
import { SetQuote } from '@quoting/state/actions/quoting-module.actions';

@Component({
  selector: 'app-custom-supply-details',
  templateUrl: './custom-supply-details.component.html',
  styleUrls: ['./custom-supply-details.component.scss'],
})
export class CustomSupplyDetailsComponent implements OnInit, OnDestroy {
  public Commodity = CommodityType;
  public dateFormat = DateConstants.LOCALE_FORMAT_SHORT();
  public quoteRequestId: any;
  public plcNsplRequirement: IPlcNsplRequirement;
  public lookingForZip: boolean;
  public generatingLoadProfileData: boolean;

  private initialFormValue = {};

  @ViewChild(ApplyToAllComponent) applyToAll: ApplyToAllComponent;
  quoteRequestState: any;
  supplyDetails: SupplyDetails[] = [];
  dataSource = new MatTableDataSource<SupplyDetails>(this.supplyDetails);
  displayedColumns = [];
  userIdentity: UtilityHubIdentity;
  signalRLoadProfileData$: Subscription;

  // TODO - SALES360-669 - Estimated consumption removed from supplier columns for manual custom route
  supplierDisplayedColumns = [
    'utilityAccountNumber',
    'rateCode',
    'peakLoadContribution',
    'plcLoadFactorPercentage',
    'plcErrors',
    'networkServicePeakLoad',
    'nsplLoadFactorPercentage',
    'nsplErrors',
    'newContractStartMonth',
    'meterZipCode',
    'uploadConsumption',
    'days',
    'consumptionStartDate',
    'consumptionEndDate',
    'meterReadings',
    'remove',
  ];

  // TODO - SALES360-669 - Estimated consumption removed from brokerage columns for manual custom route
  defaultDisplayColumns = [
    'utilityAccountNumber',
    'rateCode',
    'peakLoadContribution',
    'networkServicePeakLoad',
    'newContractStartMonth',
    'meterZipCode',
    'uploadConsumption',
    'days',
    'consumptionStartDate',
    'consumptionEndDate',
    'meterReadings',
    'remove',
  ];
  route$: Subscription;
  selectLoggedInUser$: Subscription;
  selectLoggedInUtilityHubIdentity$: Subscription;
  load$: Subscription;
  saveSupplyDetails$: Subscription;
  deleteUtility$: Subscription;
  dialogAfterClosed$: Subscription;
  deleteUtilityAfterClosed$: Subscription;
  lookupState$: Subscription;
  loadProfile$: Subscription;
  isNavButtonsDisabled: boolean = false;
  salesType$: Observable<string>;
  usageSources$: Subscription;
  usageSourceEnum = UsageSource;
  salesType: string;
  private logger: Logger;
  isDisabled$: Observable<UsageSource>;
  utilityUOM$: Observable<string>;
  isPlcBelowThreshold: boolean;
  isNsplBelowThreshold: boolean;
  maximumLoadFactorPercentageIgnored: boolean = false;
  isReadOnly$: Subscription;
  isReadOnly: boolean;
  editableSupplyDetailsStatuses: QuoteRequestStatus[] = [
    QuoteRequestStatus.InProgress, 
    QuoteRequestStatus.UsageRequested, 
    QuoteRequestStatus.UsageReceived, 
    QuoteRequestStatus.UsageUploaded
  ];

  constructor(
    private route: ActivatedRoute,
    private wizardService: WizardService,
    private customQuoteService: CustomService,
    private quoteRequestService: QuoteRequestService,
    private usLookup: UsAddressLookupService,
    protected dialog: MatDialog,
    private router: Router,
    private store: Store<AppState>,
    private pricingSourceService: PricingSourceService
  ) {
    this.logger = Logger.for(this);
    this.salesType$ = store.select(salesTypeSelector);
    this.utilityUOM$ = this.store.select(utilityUOMSelector);
  }

  ngOnDestroy(): void {
    this.signalRLoadProfileData$?.unsubscribe();
    this.route$?.unsubscribe();
    this.selectLoggedInUser$?.unsubscribe();
    this.selectLoggedInUtilityHubIdentity$?.unsubscribe();
    this.load$?.unsubscribe();
    this.saveSupplyDetails$?.unsubscribe();
    this.deleteUtility$?.unsubscribe();
    this.dialogAfterClosed$?.unsubscribe();
    this.deleteUtilityAfterClosed$?.unsubscribe();
    this.lookupState$?.unsubscribe();
    this.loadProfile$?.unsubscribe();
    this.usageSources$?.unsubscribe();
    this.isReadOnly$?.unsubscribe();
  }

  ngOnInit(): void {
    this.wizardService.bindStep(this.route, (args) => this.save(args));

    this.supplyDetails.forEach((sd) => {
      sd.loadingLoadProfile = true;
    });

    this.route$ = this.route.params.subscribe((x) => {
      this.quoteRequestId = null;
      if (!isNaN(x.quoteRequestId)) {
        this.quoteRequestId = Number(x.quoteRequestId);
        this.load();
      }
    });

    this.selectLoggedInUser$ = this.store.select(loggedInUserSelector).subscribe((user) => {
      if (user && !user.expired) {
        this.selectLoggedInUtilityHubIdentity$ = this.store.select(loggedInUtilityHubIdentitySelector).subscribe((uhUser) => {
          this.userIdentity = uhUser;

          if (this.userIdentity.isSupplierManagerOrSalesManager) this.displayedColumns = this.supplierDisplayedColumns;
          else this.displayedColumns = this.defaultDisplayColumns;
        });
      }
    });

    this.isReadOnly$ = this.store.select(isReadOnlySelector).subscribe((isReadOnly) => {
      this.isReadOnly = isReadOnly;
    });
  }

  isPlcBelowCustomerThreshold(data: SupplyDetails): boolean {
    return data.plcLoadFactorPercentage <= data.plcLoadFactorPercentageThreshold && data.plcLoadFactorPercentage >= 0;
  }

  isNsplBelowCustomerThreshold(data: SupplyDetails): boolean {
    return data.nsplLoadFactorPercentage <= data.nsplLoadFactorPercentageThreshold && data.plcLoadFactorPercentage >= 0;
  }

  load() {
    this.load$ = forkJoin({
      state: this.quoteRequestService.getQuoteRequestState(this.quoteRequestId),
      supplyDetails: this.customQuoteService.getSupplyDetails(this.quoteRequestId),
    }).subscribe((res) => {
      this.quoteRequestState = res.state;
      this.store.dispatch(new SetCommodityType(this.quoteRequestId, this.quoteRequestState.commodity));
      this.store.dispatch(new SetSalesType(this.quoteRequestId, res.state?.saleType));
      this.store.dispatch(new SetQuote(res.state, this.userIdentity, res.state.hasIDontKnows));

      this.maximumLoadFactorPercentageIgnored = res.supplyDetails[0].maximumLoadFactorPercentageIgnored;
      this.supplyDetails = res.supplyDetails;

      // Determine whether to make 'LoadFactorPercentage' and 'NetworkServicePeakLoad' textboxes read-only
      this.usageSources$ = this.store.select(usageSourcesSelector).subscribe((x) => {
        this.supplyDetails.forEach((y) => {
          y.usageSource = x.find((u) => u.utilityAcctNumber === y.utilityAccountNumber)?.usageSource ?? UsageSource.FILE_UPLOAD;
        });
      });

      this.calculateLoadFactorPercentages();
      this.supplyDetails.forEach((x) => {
        x.validZipCode = true;
        x.networkServicePeakLoad =
          x.networkServicePeakLoad != null ? Math.round(x.networkServicePeakLoad * 100) / 100 : x.networkServicePeakLoad;
        x.peakLoadContribution = x.peakLoadContribution != null ? Math.round(x.peakLoadContribution * 100) / 100 : x.peakLoadContribution;
      });
      // Resolve whether PLC or NSPL is required to be shown.
      this.plcNsplRequirement = ResolvePlcNsplRequirement(res.state.iso);
      if (!this.plcNsplRequirement.plc || this.quoteRequestState.commodity == CommodityType.Gas) {
        this.displayedColumns.splice(this.displayedColumns.indexOf('peakLoadContribution'), 1);
        this.displayedColumns.splice(this.displayedColumns.indexOf('plcLoadFactorPercentage'), 1);
        this.displayedColumns.splice(this.displayedColumns.indexOf('plcErrors'), 1);
        this.supplyDetails.forEach((x) => (x.peakLoadContribution = null));
      }
      if (!this.plcNsplRequirement.nspl || this.quoteRequestState.commodity == CommodityType.Gas) {
        this.displayedColumns.splice(this.displayedColumns.indexOf('networkServicePeakLoad'), 1);
        this.displayedColumns.splice(this.displayedColumns.indexOf('nsplLoadFactorPercentage'), 1);
        this.displayedColumns.splice(this.displayedColumns.indexOf('nsplErrors'), 1);
        this.supplyDetails.forEach((x) => (x.networkServicePeakLoad = null));
      }
      this.pricingSourceService.getPricingSource(QuoteRequestType.Custom, this.quoteRequestState.commodity).subscribe((ps) => {
        if (ps.source === PricingSource.Dynamic) {
          if (!this.quoteRequestState.hasIDontKnows) {
            this.generatingLoadProfileData = true;
            this.loadProfile$ = this.quoteRequestService.generateLoadProfile(this.quoteRequestId).subscribe(
              (profileData) => {
                this.supplyDetails.forEach((sd) => {
                  const profile = profileData?.loadProfiles?.find((x) => x.utilityAccountNumber === sd.utilityAccountNumber);
                  // sd.estimatedConsumption = profile?.annualLoad ?? sd.estimatedConsumption;
                  if (sd.estimatedConsumption === 0) {
                    sd.loadingLoadProfile = !!profileData;
                  }
                  sd.loadingLoadProfile = false;
                  this.calculateLoadFactorPercentages();
                });
                this.generatingLoadProfileData = false;
              },
              (err) => {
                this.logger.error(err);
                this.generatingLoadProfileData = false;
              }
            );
          }
        }
        this.dataSource = new MatTableDataSource(this.supplyDetails);
        this.initialFormValue = this.getDataSnapshot();
      });
    });
  }

  calculateLoadFactorPercentages() {
    this.supplyDetails.forEach((sd) => {
      this.calculatePlcLoadFactorPercentage(sd);
      this.calculateNsplLoadFactorPercentage(sd);
    });
  }

  validatePageData() {
    this.salesType$
      .subscribe((x) => {
        this.salesType = x;
      })
      .unsubscribe();
      
    if (this.salesType === SalesType.renewal || this.maximumLoadFactorPercentageIgnored) {
      return true;
    } else {
      // IACBPT1-1236 - plc/icap/nsple percentages and thresholds are not required for brokerages as they might not know them
      if (!this.userIdentity.isSupplierManagerOrSalesManager) return true;

      let valid = false;
      this.supplyDetails.forEach((supplyDetail) => {
        const plcValid = this.isPlcBelowCustomerThreshold(supplyDetail) || !this.plcNsplRequirement.plc;
        const nsplValid = this.isNsplBelowCustomerThreshold(supplyDetail) || !this.plcNsplRequirement.nspl;

        valid = plcValid && nsplValid && supplyDetail.validZipCode;
      });
      return valid;
    }
  }

  getSupplyDetailsSaveModel() {
    const details: SupplyDetailsModel[] = [];
    this.supplyDetails.forEach((element) => {
      const detail = {
        quoteRequestId: this.quoteRequestId,
        utilityAccountNumber: element.utilityAccountNumber,
        meterZipCode: element.meterZipCode,
        peakLoadContribution: element.peakLoadContribution,
        plcLoadFactorPercentage: element.plcLoadFactorPercentage,
        networkServicePeakLoad: element.networkServicePeakLoad,
        nsplLoadFactorPercentage: element.nsplLoadFactorPercentage,
        maximumLoadFactorPercentageIgnored: this.maximumLoadFactorPercentageIgnored,
      } as SupplyDetailsModel;
      details.push(detail);
    });
    return details;
  }

  public save(args: NavigateArgs): Observable<any> {
    if (this.isReadOnly && !this.userIdentity.isSupplierManagerOrSalesManager) {
      return of(true);
    }
  
    return this.salesType$.pipe(
      switchMap((salesType) => {
        if (this.generatingLoadProfileData && salesType !== SalesType.renewal) {
          this.dialog.open(AlertComponent, {
            width: '30%',
            data: {
              title: 'Load Profile',
              message: 'Please wait for the load profile to be generated',
            },
          });
          return of(false);
        } else {
          return new Observable((obs) => {
            
            if (this.validatePageData()) {
              let model: SupplyDetailsModel[];
              model = this.getSupplyDetailsSaveModel();
              this.saveSupplyDetails$ = this.customQuoteService.saveSupplyDetails(this.quoteRequestId, model).subscribe(
                (quoteRequestId) => {
                  if (!this.quoteRequestId) {
                    this.router.navigate(['custom', quoteRequestId, 'company-details']).then(() => {
                      this.route$ = this.route.params.subscribe(() => {
                        this.wizardService.bindStep(this.route, (args) => this.save(args));
                        obs.next(true);
                        obs.complete();
                      });
                    });
                  } else {
                    obs.next(true);
                    obs.complete();
                  }
                },
                (error) => {
                  obs.next(false);
                  obs.complete();
                }
              );
            } else {
              if (
                (args.direction && args.direction < 0) ||
                this.wizardService.clickedStepIsPreviousStep(CustomConstants.wizardSteps, args.step)
              ) {
                obs.next(true);
              } else {
                obs.next(false);
              }
              obs.complete();
            }
          });
        }
      })
    );
  }

  calculatePlcLoadFactorPercentage(detail: SupplyDetails) {
    if (!detail.peakLoadContribution || +detail.peakLoadContribution === 0) {
      detail.plcLoadFactorPercentage = 0;
    } else {
      detail.plcLoadFactorPercentage = parseFloat(((detail.estimatedConsumption / (8760 * detail.peakLoadContribution)) * 100).toFixed(10));
    }
  }

  calculateNsplLoadFactorPercentage(detail: SupplyDetails) {
    if (!detail.networkServicePeakLoad || +detail.networkServicePeakLoad === 0) {
      detail.nsplLoadFactorPercentage = 0;
    } else {
      detail.nsplLoadFactorPercentage = parseFloat(
        ((detail.estimatedConsumption / (8760 * detail.networkServicePeakLoad)) * 100).toFixed(10)
      );
    }
  }

  public getPlcFullValidationMessageStr(data: SupplyDetails) {
    if (!this.isPlcBelowCustomerThreshold(data) && data.peakLoadContribution !== undefined) {
      return `${this.plcNsplRequirement.plcLabel} cannot exceed ${data.plcLoadFactorPercentageThreshold}%. Please re-upload usage data in the Usage Upload page to match the chosen ${this.plcNsplRequirement.plcLabel}, or change the ${this.plcNsplRequirement.plcLabelShort} selection`;
    }
  }

  public getNsplFullValidationMessageStr(data: SupplyDetails) {
    if (!this.isNsplBelowCustomerThreshold(data) && data.peakLoadContribution !== undefined) {
      return `NSPL Load Factor cannot exceed ${data.nsplLoadFactorPercentageThreshold}%. Please re-upload usage data in the Usage Upload page to match the chosen Network Service Peak Load (NSPL), or change the NSPL selection`;
    }
  }

  deleteUtilityAccount(utilityAccountNumber: string) {
    const dialogRef = this.dialog.open(ConfirmComponent, {
      width: '25%',
      data: {
        title: 'Delete Utility Account Number',
        message: 'Are you sure you want to delete this Utility Account Number and all data associated with it?',
        primaryButtonText: 'Delete',
      },
    });

    this.deleteUtilityAfterClosed$ = dialogRef.afterClosed().subscribe((confirmed) => {
      if (confirmed) {
        const warningDialogRef = this.wizardService.resolveExpireQuoteWarningDialog();
        if (warningDialogRef.required) {
          this.dialogAfterClosed$ = warningDialogRef.dialogRef.afterClosed().subscribe((cfrmWarning) => {
            if (cfrmWarning) {
              this.deleteUtility$ = this.customQuoteService
                .deleteUtilityAccountNumber(this.quoteRequestId, utilityAccountNumber)
                .subscribe(() => {
                  this.load();
                });
            }
          });
        }
      }
    });
  }

  isZipCodeValid(supplyDetail: SupplyDetails) {
    this.setZipCodeValid(supplyDetail.meterZipCode, supplyDetail);
  }

  setZipCodeValid(newZipCode: string, supplyDetail: SupplyDetails) {
    // TODO - requires refactor to remove duplicate logic for zip code lookup
    this.lookingForZip = true;
    supplyDetail.meterZipCode = newZipCode;
    if (newZipCode.length === 0) {
      supplyDetail.zipCodeError = 'Please enter a Zip Code';
      supplyDetail.validZipCode = false;
      return;
    }
    if (newZipCode.length < 5) {
      supplyDetail.zipCodeError = 'Minimum length is 5 characters';
      supplyDetail.validZipCode = false;
      return;
    }
    if (newZipCode.length > 7) {
      supplyDetail.zipCodeError = 'Maximum length is 7 characters';
      supplyDetail.validZipCode = false;
      return;
    }

    this.lookupState$ = this.usLookup.lookupStateFromZipCode(newZipCode).subscribe(
      (results) => {
        this.lookingForZip = false;
        if (results.success) {
          this.applyToAll.generateApplyToAllPrompt('Meter Zip Code', supplyDetail.meterZipCode, 'meterZipCode');
          supplyDetail.validZipCode = true;
          return supplyDetail.validZipCode;
        }

        supplyDetail.zipCodeError = results.error;
        supplyDetail.zipCodeError = 'Invalid Zip Code. Please try again';
        supplyDetail.validZipCode = false;

        return supplyDetail.validZipCode;
      },
      () => {
        supplyDetail.zipCodeError = 'Sorry, the zip code lookup service is not available';
        supplyDetail.validZipCode = false;
        this.lookingForZip = false;
      }
    );
  }

  public next() {
    this.wizardService.next(this.isQuoteRequestInvalidated());
  }

  public back() {
    this.wizardService.previous();
  }

  public isQuoteRequestInvalidated() {
    this.salesType$
      .subscribe((x) => {
        this.salesType = x;
      })
      .unsubscribe();
    if (this.salesType === SalesType.renewal) {
      return false;
    } else {
      return this.hasFormChanged() && !this.isSupplyDetailsEditable();
    }
  }

  private isSupplyDetailsEditable(): boolean {
    return this.editableSupplyDetailsStatuses.includes(this.quoteRequestState?.status);
  }
  

  public hasFormChanged() {
    if (!this.dataSource || this.generatingLoadProfileData) {
      return false;
    }

    // initial form values differ even with no changes.  null !== undefined and loadingLoadProfile is missing from initialFormValue
    function customizer(objValue, othValue) {
      if ((objValue === null && othValue === undefined) || (objValue === undefined && othValue === null)) {
        return true;
      }
    }
    let clone = cloneDeep(this.dataSource.data);
    clone.forEach((sd) => {
      delete sd.loadingLoadProfile;
    });
    return !isEqualWith(this.initialFormValue, clone, customizer) || this.quoteRequestState?.status === QuoteRequestStatus.InProgress;
  }

  public getDataSnapshot(): SupplyDetails[] {
    const snapshot = [];
    this.dataSource.data.forEach((sd) => snapshot.push(Object.assign({}, sd)));
    return snapshot;
  }

  public showOverrideDialog(data): void {
    if (!this.maximumLoadFactorPercentageIgnored) {
      const dialogRef = this.dialog.open(ConfirmComponent, {
        width: '40%',
        data: {
          title: `Would you like to override the load factor validation for ${data.utilityAccountNumber}?`,
          message: 'The load factor value is out of range. Please select if you want to override it.',
          primaryButtonText: 'Confirm',
        },
      });

      dialogRef.afterClosed().subscribe((result) => {
        this.maximumLoadFactorPercentageIgnored = result;

        // Wizard service not updating so doing it manually
        this.wizardService.stepClicked(this.wizardService.currentStep, false);
      });
    }
  }
}
